"use client";
import { useState } from "react";
import { BoardCard } from "@/lib/data";
import AddProjectModal from "./AddProjectModal";
import BoardCardComponent from "./BoardCard";

const columnConfig = [
  {
    key: "backlog",
    label: "Backlog",
    statuses: ["backlog", "planning", "brainstorm"],
    accentBorder: "border-t-[#b0a090]",
    dot: "bg-[#b0a090]",
    countBg: "bg-[#f0ede8] text-[#8a7a68]",
  },
  {
    key: "inprogress",
    label: "In Progress",
    statuses: ["active"],
    accentBorder: "border-t-[#b5622a]",
    dot: "bg-[#b5622a]",
    countBg: "bg-[#fdf7f0] text-[#b5622a]",
  },
  {
    key: "waiting",
    label: "Waiting",
    statuses: ["waiting", "blocked"],
    accentBorder: "border-t-[#8a6fbd]",
    dot: "bg-[#8a6fbd]",
    countBg: "bg-[#f5f2fb] text-[#6d559c]",
  },
  {
    key: "done",
    label: "Done",
    statuses: ["done"],
    accentBorder: "border-t-[#3a8a5a]",
    dot: "bg-[#3a8a5a]",
    countBg: "bg-[#f0faf4] text-[#3a8a5a]",
  },
  {
    key: "wont-do",
    label: "Won't Do",
    statuses: ["wont_do"],
    accentBorder: "border-t-[#8a7a68]",
    dot: "bg-[#8a7a68]",
    countBg: "bg-[#f0ede8] text-[#6b5c48]",
  },
];

export default function ProjectsBoard({ cards }: { cards: BoardCard[] }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-10 py-6 border-b border-[#e8e2d8] flex-shrink-0">
        <div className="flex items-baseline gap-4">
          <h1 className="text-[28px] font-serif font-normal text-[#2c2416] tracking-tight">
            Projects
          </h1>
          <span className="text-sm text-[#a0906e] italic">
            {cards.length} card{cards.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-2 bg-[#b5622a] text-white text-[13px] font-medium rounded-lg hover:bg-[#9e5424] transition-colors shadow-sm"
        >
          <span className="text-base leading-none">+</span>
          New Card
        </button>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-auto">
        <div className="flex gap-5 px-10 py-6 min-w-[700px] items-start">
          {columnConfig.map((col) => {
            const colCards = cards.filter((c) => col.statuses.includes(c.status));
            return (
              <div key={col.key} className="flex flex-col w-[300px] flex-shrink-0">
                {/* Column header */}
                <div className={`sticky top-0 z-10 bg-white/95 backdrop-blur border border-[#e8e2d8] border-t-4 ${col.accentBorder} rounded-lg px-4 py-3 mb-3 flex items-center justify-between shadow-[0_8px_18px_rgba(44,36,22,0.05)]`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                    <span className="text-[13px] font-semibold text-[#2c2416] tracking-wide">{col.label}</span>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${col.countBg}`}>
                    {colCards.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="flex flex-col gap-3">
                  {colCards.map((card) => (
                    <BoardCardComponent key={card.id} card={card} />
                  ))}
                  {colCards.length === 0 && (
                    <div className="border-2 border-dashed border-[#e8e2d8] rounded-lg p-6 text-center text-[12px] text-[#c0b8b0] italic">
                      Nothing here yet
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && <AddProjectModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
